import React from "react";
import i18next from "i18next";
import { Radio, Table } from "semantic-ui-react";
import Dialog from "src/components/Dialogs/Dialog";
import { Button as ActionButton } from "src/components/Button";

// =====================================================
// MAIN INTERFACE
// =====================================================

export interface ITextTabModalsProps {
  t: i18next.TranslationFunction;

  // ── Edit modal ─────────────────────────────────
  editingText?: any;
  editContent: string;
  isEditDialogOpen: boolean;
  isConfirmDialogOpen: boolean;
  isSavingEdit: boolean;
  isEditChanged: boolean;
  onEditContentChange: (value: string) => void;
  onEditClose: () => void;
  onEditContinue: () => void;
  onEditBack: () => void;
  onEditSave: () => void;

  // ── Merge modal ────────────────────────────────
  selectedRows: any[];
  usagesData: { [textId: number]: any[] };
  isMergePreviewOpen: boolean;
  isMergeConfirmOpen: boolean;
  mergeCanonicalId: number | null;
  isMerging: boolean;
  onMergeCanonicalChange: (id: number) => void;
  onMergePreviewClose: () => void;
  onMergePreviewConfirm: () => void;
  onMergeConfirmBack: () => void;
  onMergeConfirmClose: () => void;
  onMergeExecute: () => void;

  // ── Search & Replace modal ─────────────────────
  isSearchReplaceOpen: boolean;
  isSearchReplacePreviewOpen: boolean;
  isSearchReplaceConfirmOpen: boolean;
  searchTerm: string;
  replaceTerm: string;
  searchReplacePreviewData: any;
  isSearchReplacing: boolean;
  onSearchTermChange: (value: string) => void;
  onReplaceTermChange: (value: string) => void;
  onSearchReplaceClose: () => void;
  onSearchReplacePreview: () => void;
  onSearchReplacePreviewBack: () => void;
  onSearchReplacePreviewConfirm: () => void;
  onSearchReplacePreviewClose: () => void;
  onSearchReplaceConfirmBack: () => void;
  onSearchReplaceConfirmClose: () => void;
  onSearchReplaceExecute: () => void;
}

// =====================================================
// HELPERS
// =====================================================

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Wraps the "permanently deleted" phrase (EN/DE) in a red span within a translated string. */
const highlightDeletedPhrase = (text: string): React.ReactNode => {
  const pattern = /(permanently deleted|dauerhaft gelöscht)/i;
  const parts = text.split(pattern);
  if (parts.length === 1) {
    return <>{text}</>;
  }
  return (
    <>
      {parts.map((part, i) =>
        pattern.test(part)
          ? <span key={i} className="text-merge-confirm-deleted">{part}</span>
          : part,
      )}
    </>
  );
};

const highlightText = (
  text: string,
  term: string,
  bg: string,
): React.ReactNode => {
  if (!term || !text) return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(term)})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <mark key={i} style={{ backgroundColor: bg, padding: 0 }}>
        {part}
      </mark>
    ) : (
      part
    ),
  );
};

// =====================================================
// TEXT EDIT MODAL
// =====================================================

const TextEditModal: React.FC<
  Pick<
    ITextTabModalsProps,
    | "t"
    | "editingText"
    | "editContent"
    | "isEditDialogOpen"
    | "isEditChanged"
    | "onEditContentChange"
    | "onEditClose"
    | "onEditContinue"
  >
> = ({
  t,
  editingText,
  editContent,
  isEditDialogOpen,
  isEditChanged,
  onEditContentChange,
  onEditClose,
  onEditContinue,
}) => {
  if (!isEditDialogOpen || !editingText) {
    return null;
  }
  return (
    <Dialog
      open={true}
      title={t("MANAGEMENT_VIEW:TEXT_EDIT_CONTENT")}
      customStyleClassName="BaseModal text-edit-dialog"
      onRequestClose={onEditClose}
      actions={[
        <ActionButton
          key="continue"
          icon="arrow right"
          disabled={!isEditChanged}
          onClick={onEditContinue}
        />,
        <ActionButton key="cancel" icon="ban" onClick={onEditClose} />,
      ]}
    >
      <div style={{ marginBottom: "12px" }}>
        <b>{t("TEXT_ID")}:</b> {editingText.objectId}
      </div>
      <div style={{ marginBottom: "8px", fontWeight: 600 }}>
        {t("MANAGEMENT_VIEW:TEXT_CONTENT")}
      </div>
      <textarea
        className="text-edit-content-input"
        value={editContent}
        onChange={(e) => onEditContentChange(e.target.value)}
      />
    </Dialog>
  );
};

// =====================================================
// TEXT EDIT CONFIRM MODAL
// =====================================================

const TextEditConfirmModal: React.FC<
  Pick<
    ITextTabModalsProps,
    | "t"
    | "editingText"
    | "editContent"
    | "isConfirmDialogOpen"
    | "isSavingEdit"
    | "onEditClose"
    | "onEditBack"
    | "onEditSave"
  >
> = ({
  t,
  editingText,
  editContent,
  isConfirmDialogOpen,
  isSavingEdit,
  onEditClose,
  onEditBack,
  onEditSave,
}) => {
  if (!isConfirmDialogOpen || !editingText) {
    return null;
  }
  return (
    <Dialog
      open={true}
      title={t("MANAGEMENT_VIEW:TEXT_CONFIRM_UPDATE")}
      customStyleClassName="BaseModal text-edit-dialog"
      onRequestClose={onEditClose}
      actions={[
        <ActionButton
          key="back"
          icon="arrow left"
          className="text-dialog-back-btn"
          disabled={isSavingEdit}
          onClick={onEditBack}
        />,
        <ActionButton
          key="confirm"
          icon="checkmark"
          disabled={isSavingEdit}
          onClick={onEditSave}
        />,
        <ActionButton
          key="cancel"
          icon="ban"
          disabled={isSavingEdit}
          onClick={onEditClose}
        />,
      ]}
    >
      <p>
        {t("MANAGEMENT_VIEW:TEXT_CHANGE")} <b>{editingText.usageCount || 0}</b>{" "}
        {t("MANAGEMENT_VIEW:TEXT_USAGE")}{" "}
        <b>{editingText.modelYearCount || 0}</b>{" "}
        {t("BUSINESS_APPROVAL_MODEL_YEAR")},{" "}
        <b>{editingText.typeFamilyCount || 0}</b>{" "}
        {t("BUSINESS_APPROVAL_FAMILY")},{" "}
        <b>{editingText.derivativeCount || 0}</b> {t("TEXT_DERIVATES")}.
      </p>
      <div style={{ marginTop: "16px" }}>
        <div style={{ marginBottom: "6px", fontWeight: 600 }}>
          {t("MANAGEMENT_VIEW:TEXT_NEW_CONTENT")}
        </div>
        <div className="text-confirm-content-box">{editContent.trim()}</div>
      </div>
    </Dialog>
  );
};

// =====================================================
// MERGE PREVIEW MODAL
// =====================================================

const MergePreviewModal: React.FC<
  Pick<
    ITextTabModalsProps,
    | "t"
    | "selectedRows"
    | "usagesData"
    | "isMergePreviewOpen"
    | "mergeCanonicalId"
    | "isMerging"
    | "onMergeCanonicalChange"
    | "onMergePreviewClose"
    | "onMergePreviewConfirm"
  >
> = ({
  t,
  selectedRows,
  usagesData,
  isMergePreviewOpen,
  mergeCanonicalId,
  isMerging,
  onMergeCanonicalChange,
  onMergePreviewClose,
  onMergePreviewConfirm,
}) => {
  if (!isMergePreviewOpen) {
    return null;
  }

  const maxUsage = Math.max(...selectedRows.map((r) => r.usageCount || 0));
  const mergeRows = selectedRows.filter((r) => r.objectId !== mergeCanonicalId);
  const mergeIds = mergeRows.map((r) => r.objectId);
  const totalRepointed = mergeRows.reduce(
    (sum, r) => sum + (r.usageCount || 0),
    0,
  );

  return (
    <Dialog
      open={true}
      fullWidth={true}
      title={t("MANAGEMENT_VIEW:MERGE_PREVIEW_TITLE")}
      customStyleClassName="text-merge-dialog"
      onRequestClose={onMergePreviewClose}
      actions={[
        <ActionButton
          key="cancel"
          icon="ban"
          className="text-dialog-back-btn"
          disabled={isMerging}
          onClick={onMergePreviewClose}
        />,
        <ActionButton
          key="confirm"
          icon="checkmark"
          disabled={!mergeCanonicalId || isMerging}
          onClick={onMergePreviewConfirm}
        />,
      ]}
    >
      <p className="text-merge-intro">
        {t("MANAGEMENT_VIEW:MERGE_INTRO_KEEP")}{' '}
        {t("MANAGEMENT_VIEW:MERGE_INTRO_REPOINT")}{' '}
        {t("MANAGEMENT_VIEW:MERGE_INTRO_REMOVE")}
      </p>

      <div className="text-merge-section-label">
        {t("MANAGEMENT_VIEW:MERGE_SECTION_CANONICAL")}
      </div>
      <div className="text-merge-texts-list">
        {selectedRows.map((row) => {
          const isCanonical = row.objectId === mergeCanonicalId;
          const isHighest = (row.usageCount || 0) === maxUsage;
          return (
            <div
              key={row.objectId}
              className={`text-merge-text-card${isCanonical ? " is-canonical" : ""}`}
              onClick={() => onMergeCanonicalChange(row.objectId)}
            >
              <Radio
                checked={isCanonical}
                onChange={() => onMergeCanonicalChange(row.objectId)}
              />
              <div className="text-merge-text-card-body">
                <div className="text-merge-text-card-header">
                  <span>
                    Text-ID {row.objectId} · {row.usageCount || 0} {t("MANAGEMENT_VIEW:MERGE_USAGES")}
                  </span>
                  {isHighest && (
                    <span className="text-merge-badge-highest">
                      {t("MANAGEMENT_VIEW:MERGE_BADGE_HIGHEST")} ★
                    </span>
                  )}
                </div>
                <div className="text-merge-text-card-content">
                  {row.displayContent}
                </div>
                <div className="text-merge-text-card-meta">
                  {row.modelYearCount || 0} {t("BUSINESS_APPROVAL_MODEL_YEAR")} ·{" "}
                  {row.typeFamilyCount || 0} {t("BUSINESS_APPROVAL_FAMILY")} · {row.derivativeCount || 0}{" "}
                  {t("TEXT_DERIVATES")}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {mergeCanonicalId && (
        <>
          <div className="text-merge-impact-summary">
            ⚠ <b>{totalRepointed}</b> {t("MANAGEMENT_VIEW:MERGE_REPOINT_FROM")}{mergeIds.length > 1 ? "s" : ""}{" "}
            {mergeIds.join(", ")} → Text-ID <b>{mergeCanonicalId}</b>.{" "}
            <b>{mergeIds.length}</b> {t("MANAGEMENT_VIEW:MERGE_DELETE_WARNING")}
          </div>

          <div className="text-merge-section-label">
            {t("MANAGEMENT_VIEW:MERGE_SECTION_IMPACT")}
          </div>
          <div className="text-merge-impact-wrapper">
            <Table celled={true} compact="very" size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("BUSINESS_APPROVAL_MODEL_YEAR")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("BUSINESS_APPROVAL_FAMILY")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("MANAGEMENT_VIEW:TEXT_I_NUMBER")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("TEXT_DERIVATES")}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(() => {
                  const impactRows: React.ReactNode[] = [];
                  mergeRows.forEach((row) => {
                    const yearList = usagesData[row.objectId] || [];
                    yearList.forEach((year: any) => {
                      (year.typeFamilies || []).forEach((tf: any) => {
                        (tf.derivatives || []).forEach((der: any) => {
                          impactRows.push(
                            <Table.Row key={`${row.objectId}-${year.modelYearId}-${tf.typeFamilyId}-${der.derivativeId}`}>
                              <Table.Cell>{year.modelYear}</Table.Cell>
                              <Table.Cell>{tf.typeFamilyName}</Table.Cell>
                              <Table.Cell>{Array.isArray(der.inumbers) ? der.inumbers.join(', ') : '—'}</Table.Cell>
                              <Table.Cell>
                                {der.derivativeName && der.btyp
                                  ? `${der.derivativeName} (${der.btyp})`
                                  : (der.derivativeName || '—')}
                              </Table.Cell>
                            </Table.Row>,
                          );
                        });
                      });
                    });
                  });
                  return impactRows;
                })()}
              </Table.Body>
            </Table>
          </div>
        </>
      )}
    </Dialog>
  );
};

// =====================================================
// MERGE CONFIRM MODAL
// =====================================================

const MergeConfirmModal: React.FC<
  Pick<
    ITextTabModalsProps,
    | "t"
    | "selectedRows"
    | "isMergeConfirmOpen"
    | "mergeCanonicalId"
    | "isMerging"
    | "onMergeConfirmBack"
    | "onMergeConfirmClose"
    | "onMergeExecute"
  >
> = ({
  t,
  selectedRows,
  isMergeConfirmOpen,
  mergeCanonicalId,
  isMerging,
  onMergeConfirmBack,
  onMergeConfirmClose,
  onMergeExecute,
}) => {
  if (!isMergeConfirmOpen) {
    return null;
  }
  const mergeRows = selectedRows.filter((r) => r.objectId !== mergeCanonicalId);
  const totalRepointed = mergeRows.reduce(
    (sum, r) => sum + (r.usageCount || 0),
    0,
  );

  return (
    <Dialog
      open={true}
      title={t("MANAGEMENT_VIEW:TEXT_CONFIRM_UPDATE")}
      customStyleClassName="BaseModal text-edit-dialog"
      onRequestClose={onMergeConfirmClose}
      actions={[
        <ActionButton
          key="back"
          icon="arrow left"
          className="text-dialog-back-btn"
          disabled={isMerging}
          onClick={onMergeConfirmBack}
        />,
        <ActionButton
          key="confirm"
          icon="checkmark"
          disabled={isMerging}
          onClick={onMergeExecute}
        />,
        <ActionButton
          key="cancel"
          icon="ban"
          disabled={isMerging}
          onClick={onMergeConfirmClose}
        />,
      ]}
    >
      <div className="text-merge-confirm-body">
        <div className="text-merge-confirm-warning-icon">⚠</div>
        <div className="text-merge-confirm-lines">
          {(() => {
            const canonicalRow = selectedRows.find((r) => r.objectId === mergeCanonicalId);
            const canonicalContent = canonicalRow && canonicalRow.displayContent || '';
            const truncate = (s: string, max = 60) =>
              s.length > max ? s.slice(0, max).replace(/\s+$/, '') + '…' : s;
            return (
              <>
                <div className="text-merge-confirm-line">
                  <span className="text-merge-confirm-number">{totalRepointed}</span>
                  {' '}{t("MANAGEMENT_VIEW:MERGE_REPOINT_TO")}{' '}
                  <span
                    className="text-merge-confirm-content-ref"
                    title={canonicalContent}
                  >
                    "{truncate(canonicalContent)}"
                  </span>
                  {' '}
                  <span className="text-merge-confirm-id-hint">(Text-ID {mergeCanonicalId})</span>
                </div>
                <div className="text-merge-confirm-line danger">
                  <span className="text-merge-confirm-number">{mergeRows.length}</span>
                  {' '}{highlightDeletedPhrase(t("MANAGEMENT_VIEW:MERGE_DELETE_WARNING"))}
                  <ul className="text-merge-confirm-delete-list">
                    {mergeRows.map((r: any) => (
                      <li key={r.objectId}>
                        <span
                          className="text-merge-confirm-content-ref"
                          title={r.displayContent || ''}
                        >
                          "{truncate(r.displayContent || '')}"
                        </span>
                        {' '}
                        <span className="text-merge-confirm-id-hint">(Text-ID {r.objectId})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </Dialog>
  );
};

// =====================================================
// SEARCH & REPLACE MODAL
// =====================================================

const SearchReplaceModal: React.FC<
  Pick<
    ITextTabModalsProps,
    | "t"
    | "selectedRows"
    | "isSearchReplaceOpen"
    | "searchTerm"
    | "replaceTerm"
    | "onSearchTermChange"
    | "onReplaceTermChange"
    | "onSearchReplaceClose"
    | "onSearchReplacePreview"
  >
> = ({
  t,
  selectedRows,
  isSearchReplaceOpen,
  searchTerm,
  replaceTerm,
  onSearchTermChange,
  onReplaceTermChange,
  onSearchReplaceClose,
  onSearchReplacePreview,
}) => {
  if (!isSearchReplaceOpen) {
    return null;
  }
  return (
    <Dialog
      open={true}
      title={t("MANAGEMENT_VIEW:TEXT_SEARCH_REPLACE")}
      customStyleClassName="BaseModal text-edit-dialog"
      onRequestClose={onSearchReplaceClose}
      actions={[
        <ActionButton
          key="cancel"
          icon="ban"
          onClick={onSearchReplaceClose}
        />,
        <ActionButton
          key="preview"
          icon="arrow right"
          disabled={!searchTerm.trim()}
          onClick={onSearchReplacePreview}
        />,
      ]}
    >
      <p className="text-search-replace-info">
        {t("MANAGEMENT_VIEW:SEARCH_REPLACE_APPLIED_TO")}{" "}
        <b>{selectedRows.length} {t("MANAGEMENT_VIEW:SEARCH_REPLACE_SELECTED_TEXTS")}</b>. {t("MANAGEMENT_VIEW:SEARCH_REPLACE_FILTER_INFO")}
      </p>
      <div className="text-search-replace-field">
        <label className="text-search-replace-field-label">{t("BUSINESS_APPROVAL_SEARCH")}</label>
        <input
          className="text-search-replace-input"
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          placeholder={t("MANAGEMENT_VIEW:SEARCH_REPLACE_SEARCH_PLACEHOLDER")}
        />
      </div>
      <div className="text-search-replace-field">
        <label className="text-search-replace-field-label">{t("MANAGEMENT_VIEW:SEARCH_REPLACE_REPLACE_LABEL")}</label>
        <input
          className="text-search-replace-input"
          type="text"
          value={replaceTerm}
          onChange={(e) => onReplaceTermChange(e.target.value)}
          placeholder={t("MANAGEMENT_VIEW:SEARCH_REPLACE_REPLACE_PLACEHOLDER")}
        />
      </div>
    </Dialog>
  );
};

// =====================================================
// SEARCH & REPLACE PREVIEW MODAL
// =====================================================

const SearchReplacePreviewModal: React.FC<
  Pick<
    ITextTabModalsProps,
    | "t"
    | "selectedRows"
    | "isSearchReplacePreviewOpen"
    | "searchTerm"
    | "replaceTerm"
    | "searchReplacePreviewData"
    | "isSearchReplacing"
    | "onSearchReplacePreviewBack"
    | "onSearchReplacePreviewClose"
    | "onSearchReplacePreviewConfirm"
  >
> = ({
  t,
  selectedRows,
  isSearchReplacePreviewOpen,
  searchTerm,
  replaceTerm,
  searchReplacePreviewData,
  isSearchReplacing,
  onSearchReplacePreviewBack,
  onSearchReplacePreviewClose,
  onSearchReplacePreviewConfirm,
}) => {
  const previewItems = searchReplacePreviewData && Array.isArray(searchReplacePreviewData.items)
    ? searchReplacePreviewData.items
    : [];
  const matchingRows = previewItems.filter((item: any) => item.status === "REPLACED");
  const noMatchRows = previewItems.filter((item: any) => item.status !== "REPLACED");

  const [visibleUsageByTextId, setVisibleUsageByTextId] = React.useState<{
    [textId: number]: number;
  }>({});

  React.useEffect(
    () => {
      if (!isSearchReplacePreviewOpen) {
        return;
      }
      const initialVisible: { [textId: number]: number } = {};
      matchingRows.forEach((row: any) => {
        initialVisible[row.textId] = 3;
      });
      setVisibleUsageByTextId(initialVisible);
    },
    [isSearchReplacePreviewOpen, searchReplacePreviewData],
  );

  if (!isSearchReplacePreviewOpen) {
    return null;
  }

  const buildUsageTable = (row: any): React.ReactNode => {
    const usageSource = row.usages || {};
    const yearList = Array.isArray(usageSource.modelYears) ? usageSource.modelYears : [];
    if (yearList.length === 0) return null;
    const usageRows: React.ReactNode[] = [];
    yearList.forEach((year: any) => {
      (year.typeFamilies || []).forEach((tf: any) => {
        (tf.derivatives || []).forEach((der: any) => {
          usageRows.push(
            <Table.Row key={`${row.textId}-${year.modelYearId}-${tf.typeFamilyId}-${der.derivativeId}`}>
              <Table.Cell>{year.modelYear}</Table.Cell>
              <Table.Cell>{tf.typeFamilyName}</Table.Cell>
              <Table.Cell>{Array.isArray(der.inumbers) ? der.inumbers.join(', ') : '—'}</Table.Cell>
              <Table.Cell>{der.derivativeName && der.btyp ? `${der.derivativeName} (${der.btyp})` : (der.derivativeName || '—')}</Table.Cell>
            </Table.Row>,
          );
        });
      });
    });
    const visibleCount = visibleUsageByTextId[row.textId] || 3;
    const visible = usageRows.slice(0, visibleCount);
    const more = usageRows.length - visibleCount;
    return (
      <div className="text-merge-impact-wrapper text-sr-usage-table">
        <Table celled={true} compact="very" size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t("BUSINESS_APPROVAL_MODEL_YEAR")}</Table.HeaderCell>
              <Table.HeaderCell>{t("BUSINESS_APPROVAL_FAMILY")}</Table.HeaderCell>
              <Table.HeaderCell>{t("MANAGEMENT_VIEW:TEXT_I_NUMBER")}</Table.HeaderCell>
              <Table.HeaderCell>{t("TEXT_DERIVATES")}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visible}
            {more > 0 && (
              <Table.Row>
                <Table.Cell
                  colSpan={4}
                  textAlign="center"
                  className="text-merge-impact-empty"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setVisibleUsageByTextId((prev) => ({
                      ...prev,
                      [row.textId]: Math.min((prev[row.textId] || 3) + 3, usageRows.length),
                    }))
                  }
                >
                  ... {more} {t("MANAGEMENT_VIEW:SEARCH_REPLACE_MORE_USAGES")}
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
    );
  };

  return (
    <Dialog
      open={true}
      fullWidth={true}
      title={t("MANAGEMENT_VIEW:TEXT_SEARCH_REPLACE")}
      customStyleClassName="text-search-replace-preview-dialog"
      onRequestClose={onSearchReplacePreviewClose}
      actions={[
        <ActionButton
          key="back"
          icon="arrow left"
          className="text-dialog-back-btn"
          disabled={isSearchReplacing}
          onClick={onSearchReplacePreviewBack}
        />,
        <ActionButton
          key="confirm"
          icon="arrow right"
          disabled={isSearchReplacing || matchingRows.length === 0}
          onClick={onSearchReplacePreviewConfirm}
        />,
        <ActionButton
          key="cancel"
          icon="ban"
          disabled={isSearchReplacing}
          onClick={onSearchReplacePreviewClose}
        />,
      ]}
    >
      <div className="text-search-replace-summary-banner">
        ⚠ {t("MANAGEMENT_VIEW:SEARCH_REPLACE_REPLACING")} <b>"{searchTerm}"</b> → <b>"{replaceTerm}"</b> in{" "}
        <b>
          {searchReplacePreviewData && searchReplacePreviewData.changedTextCount || matchingRows.length}
           of {searchReplacePreviewData && searchReplacePreviewData.requestedTextCount || selectedRows.length}
            {t("MANAGEMENT_VIEW:SEARCH_REPLACE_SELECTED_TEXTS")}
        </b>.
        {noMatchRows.length > 0 && ` ${noMatchRows.length} ${t("MANAGEMENT_VIEW:SEARCH_REPLACE_NO_MATCH_INFO")}`}
      </div>

      <div className="text-search-replace-changes-wrapper">
        {matchingRows.length > 0 && (
          <div className="text-search-replace-section-label">
            {t("MANAGEMENT_VIEW:SEARCH_REPLACE_CHANGES_HEADER")} ({matchingRows.length})
          </div>
        )}
        {matchingRows.map((row: any) => {
          return (
            <div key={row.textId} className="text-search-replace-card">
              <div className="text-search-replace-card-title">
                Text-ID <b style={{ color: "#1565c0" }}>{row.textId}</b>{" · "}
                {row.usageCount || row.usages && row.usages.occurrences || 0} {t("MANAGEMENT_VIEW:MERGE_USAGES")}
              </div>
              <div className="text-search-replace-before-after">
                <div className="text-search-replace-before">
                  <div className="text-search-replace-ba-label before-label">{t("MANAGEMENT_VIEW:SEARCH_REPLACE_BEFORE")}</div>
                  <div className="text-search-replace-ba-text">
                    {highlightText(row.before || "", searchTerm, "#ffe066")}
                  </div>
                </div>
                <div className="text-search-replace-after">
                  <div className="text-search-replace-ba-label after-label">{t("MANAGEMENT_VIEW:SEARCH_REPLACE_AFTER")}</div>
                  <div className="text-search-replace-ba-text">
                    {highlightText(row.after || "", replaceTerm, "#b7f5b0")}
                  </div>
                </div>
              </div>
              {buildUsageTable(row)}
            </div>
          );
        })}

        {noMatchRows.length > 0 && (
          <div className="text-search-replace-no-match-section">
            <div className="text-search-replace-section-label">
              {t("MANAGEMENT_VIEW:SEARCH_REPLACE_NO_MATCH_FOUND")} ({noMatchRows.length})
            </div>
            <p className="text-search-replace-no-match-text">
              {noMatchRows.map((r: any) => `Text-ID ${r.textId}`).join(", ")} — {t("MANAGEMENT_VIEW:SEARCH_REPLACE_NOT_CONTAIN")}
              "{searchTerm}" {t("MANAGEMENT_VIEW:SEARCH_REPLACE_NOT_MODIFIED")}
            </p>
          </div>
        )}
      </div>
    </Dialog>
  );
};

// =====================================================
// SEARCH & REPLACE CONFIRM MODAL
// =====================================================

const SearchReplaceConfirmModal: React.FC<
  Pick<
    ITextTabModalsProps,
    | "t"
    | "selectedRows"
    | "searchTerm"
    | "replaceTerm"
    | "searchReplacePreviewData"
    | "isSearchReplaceConfirmOpen"
    | "isSearchReplacing"
    | "onSearchReplaceConfirmBack"
    | "onSearchReplaceConfirmClose"
    | "onSearchReplaceExecute"
  >
> = ({
  t,
  selectedRows,
  searchTerm,
  replaceTerm,
  searchReplacePreviewData,
  isSearchReplaceConfirmOpen,
  isSearchReplacing,
  onSearchReplaceConfirmBack,
  onSearchReplaceConfirmClose,
  onSearchReplaceExecute,
}) => {
  if (!isSearchReplaceConfirmOpen) { return null; }

  const matchingRows = searchReplacePreviewData && Array.isArray(searchReplacePreviewData.items)
    ? searchReplacePreviewData.items.filter((item: any) => item.status === "REPLACED")
    : [];
  const totalUsages = searchReplacePreviewData && typeof searchReplacePreviewData.affectedUsageCount === "number"
    ? searchReplacePreviewData.affectedUsageCount
    : matchingRows.reduce((sum: number, r: any) => sum + ((r.usages && r.usages.occurrences) || r.usageCount || 0), 0);
  const truncate = (s: string, max = 60) =>
    s.length > max ? s.slice(0, max).replace(/\s+$/, '') + '…' : s;

  return (
    <Dialog
      open={true}
      title={t("MANAGEMENT_VIEW:TEXT_CONFIRM_UPDATE")}
      customStyleClassName="BaseModal text-edit-dialog"
      onRequestClose={onSearchReplaceConfirmClose}
      actions={[
        <ActionButton key="back" icon="arrow left" className="text-dialog-back-btn" disabled={isSearchReplacing} onClick={onSearchReplaceConfirmBack} />,
        <ActionButton key="confirm" icon="checkmark" disabled={isSearchReplacing} onClick={onSearchReplaceExecute} />,
        <ActionButton key="cancel" icon="ban" disabled={isSearchReplacing} onClick={onSearchReplaceConfirmClose} />,
      ]}
    >
      <div className="text-merge-confirm-body">
        <div className="text-merge-confirm-warning-icon">⚠</div>
        <div className="text-merge-confirm-lines">
          <div className="text-merge-confirm-line">
            {t("MANAGEMENT_VIEW:SEARCH_REPLACE_REPLACING")}{' '}
            <span className="text-merge-confirm-deleted">"{searchTerm}"</span>
            {' '}→ <b>"{replaceTerm}"</b>
          </div>
          <div className="text-merge-confirm-line">
            <span className="text-merge-confirm-number">{matchingRows.length}</span>
            {' '}{t("TEXT_TAB")} ·{' '}
            <span className="text-merge-confirm-number">{totalUsages}</span>
            {' '}{t("MANAGEMENT_VIEW:SEARCH_REPLACE_USAGES_AFFECTED")}{' '}
            <span className="text-merge-confirm-deleted">{t("MANAGEMENT_VIEW:SEARCH_REPLACE_CANNOT_UNDO")}</span>
          </div>
          <ul className="text-merge-confirm-delete-list">
            {matchingRows.map((r: any) => (
              <li key={r.textId}>
                <span className="text-merge-confirm-content-ref" title={r.before || ''}>
                  "{truncate(r.before || '')}"
                </span>
                {' '}
                <span className="text-merge-confirm-id-hint">(Text-ID {r.textId})</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Dialog>
  );
};

// =====================================================
// ROOT EXPORT — renders all modals in one place
// =====================================================

const TextTabModals: React.FC<ITextTabModalsProps> = (props) => (
  <>
    <TextEditModal
      t={props.t}
      editingText={props.editingText}
      editContent={props.editContent}
      isEditDialogOpen={props.isEditDialogOpen}
      isEditChanged={props.isEditChanged}
      onEditContentChange={props.onEditContentChange}
      onEditClose={props.onEditClose}
      onEditContinue={props.onEditContinue}
    />
    <TextEditConfirmModal
      t={props.t}
      editingText={props.editingText}
      editContent={props.editContent}
      isConfirmDialogOpen={props.isConfirmDialogOpen}
      isSavingEdit={props.isSavingEdit}
      onEditClose={props.onEditClose}
      onEditBack={props.onEditBack}
      onEditSave={props.onEditSave}
    />
    <MergePreviewModal
      t={props.t}
      selectedRows={props.selectedRows}
      usagesData={props.usagesData}
      isMergePreviewOpen={props.isMergePreviewOpen}
      mergeCanonicalId={props.mergeCanonicalId}
      isMerging={props.isMerging}
      onMergeCanonicalChange={props.onMergeCanonicalChange}
      onMergePreviewClose={props.onMergePreviewClose}
      onMergePreviewConfirm={props.onMergePreviewConfirm}
    />
    <MergeConfirmModal
      t={props.t}
      selectedRows={props.selectedRows}
      isMergeConfirmOpen={props.isMergeConfirmOpen}
      mergeCanonicalId={props.mergeCanonicalId}
      isMerging={props.isMerging}
      onMergeConfirmBack={props.onMergeConfirmBack}
      onMergeConfirmClose={props.onMergeConfirmClose}
      onMergeExecute={props.onMergeExecute}
    />
    <SearchReplaceModal
      t={props.t}
      selectedRows={props.selectedRows}
      isSearchReplaceOpen={props.isSearchReplaceOpen}
      searchTerm={props.searchTerm}
      replaceTerm={props.replaceTerm}
      onSearchTermChange={props.onSearchTermChange}
      onReplaceTermChange={props.onReplaceTermChange}
      onSearchReplaceClose={props.onSearchReplaceClose}
      onSearchReplacePreview={props.onSearchReplacePreview}
    />
    <SearchReplacePreviewModal
      t={props.t}
      selectedRows={props.selectedRows}
      isSearchReplacePreviewOpen={props.isSearchReplacePreviewOpen}
      searchTerm={props.searchTerm}
      replaceTerm={props.replaceTerm}
      searchReplacePreviewData={props.searchReplacePreviewData}
      isSearchReplacing={props.isSearchReplacing}
      onSearchReplacePreviewBack={props.onSearchReplacePreviewBack}
      onSearchReplacePreviewClose={props.onSearchReplacePreviewClose}
      onSearchReplacePreviewConfirm={props.onSearchReplacePreviewConfirm}
    />
    <SearchReplaceConfirmModal
      t={props.t}
      selectedRows={props.selectedRows}
      searchTerm={props.searchTerm}
      replaceTerm={props.replaceTerm}
      searchReplacePreviewData={props.searchReplacePreviewData}
      isSearchReplaceConfirmOpen={props.isSearchReplaceConfirmOpen}
      isSearchReplacing={props.isSearchReplacing}
      onSearchReplaceConfirmBack={props.onSearchReplaceConfirmBack}
      onSearchReplaceConfirmClose={props.onSearchReplaceConfirmClose}
      onSearchReplaceExecute={props.onSearchReplaceExecute}
    />
  </>
);

export default TextTabModals;



// WEBPACK FOOTER //
// ./src/views/ManagementView/TextTabModals.tsx